
interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Standardized Toggle Switch Component
 *
 * Provides consistent toggle behavior with accessibility support.
 * Use this component for all enable/disable actions across the application.
 */
const Toggle = ({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
}: ToggleProps) => {
  const sizes = {
    sm: {
      track: 'h-5 w-9',
      thumb: 'h-4 w-4',
      translate: 'translate-x-4',
    },
    md: {
      track: 'h-6 w-11',
      thumb: 'h-5 w-5',
      translate: 'translate-x-5',
    },
    lg: {
      track: 'h-7 w-14',
      thumb: 'h-6 w-6',
      translate: 'translate-x-7',
    },
  };

  const sizeConfig = sizes[size] ?? sizes.md;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative inline-flex flex-shrink-0 cursor-pointer rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        dark:focus:ring-offset-gray-800
        ${sizeConfig.track}
        ${checked
          ? 'bg-blue-600 dark:bg-blue-500'
          : 'bg-gray-200 dark:bg-gray-600'
        }
        ${disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-opacity-90'
        }
      `}
    >
      <span className="sr-only">{label}</span>
      <span
        aria-hidden="true"
        className={`
          pointer-events-none inline-block rounded-full
          bg-white shadow ring-0 transition duration-200 ease-in-out
          ${sizeConfig.thumb}
          ${checked ? sizeConfig.translate : 'translate-x-0'}
        `}
      />
    </button>
  );
};

interface ToggleWithLabelProps extends ToggleProps {
  description?: string;
  labelPosition?: 'left' | 'right';
}

/**
 * Toggle with Label - Common pattern for labeled toggles
 */
export const ToggleWithLabel = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
  labelPosition = 'right',
}: ToggleWithLabelProps) => {
  const content = (
    <div className={labelPosition === 'right' ? 'ml-3' : 'mr-3'}>
      <span className="text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </span>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
    </div>
  );

  return (
    <div className={`flex items-center ${labelPosition === 'left' ? 'flex-row-reverse justify-end' : ''}`}>
      <Toggle
        checked={checked}
        onChange={onChange}
        label={label}
        disabled={disabled}
        size={size}
      />
      {content}
    </div>
  );
};

export default Toggle;
